/**
 * The measured error rates and caveats the interface and the exports quote, per bundled authorship model (DESIGN UD-18,
 * REFIT-B3). Nothing here is computed in the UI: each figure is copied from the model's claims table and model card,
 * and tests (test/unit/model-card-figures.test.ts for the C3 model, test/unit/refit-wiring.test.ts for the refit) check
 * the rows against the claims files, so they cannot drift from the shipped model without failing a test.
 *
 * The main thread imports this file, so it must not import the engine's `model.ts` (that bundles the model files);
 * the unreachable length groups below are copied from `unreachableGroups(model JSON)` and a test holds them equal.
 * `figuresFor(id)` is the one lookup: callers pass the analysed result's model id, or the default's.
 */
import { MODEL_CARD_FIGURES } from '../engine/ensemble/model-card-figures.ts';
import type { StratumFigure } from '../engine/ensemble/model-card-figures.ts';
import { SPEECH_CAVEAT } from '../web/strings.ts';

export type { StratumFigure };

/** A measured human rate: n documents, `flagged` of them above the reference point, one-sided 95% upper bound as a fraction. */
export interface RateFigure {
	n: number;
	flagged: number;
	bound: number;
}

/** The fixed speech caveat in its three forms (see `SPEECH_CAVEAT` in the web strings). */
export interface SpeechCaveatText {
	lead: string;
	link: string;
	short: string;
	plain: string;
}

/** A length group in which no document reaches the reference point (CD-94); the same shape as `UnreachableGroup`. */
export interface UnreachableFigure {
	/** The group's upper word limit (exclusive). */
	maxWords: number;
	/** The calibration map's maximum after the clip, on the value scale (the screen shows 100 times it). */
	ceiling: number;
	/** The reference point t* on the value scale. */
	tStar: number;
}

/**
 * A length group with its own operating point on the model's log-odds (DESIGN CD-98, CD-104): the point, the group's
 * calibration map ceiling (the index of such a text never exceeds 100 times it) and the nested human rates at the point.
 */
export interface ShortPointFigure {
	/** The group's upper word limit (exclusive). */
	maxWords: number;
	/** The point's `name` in the model file ("short-text point"). */
	name: string;
	/** The cut on the model's raw log-odds; a document is flagged at or above it. */
	threshold: number;
	/** The calibration map's maximum after the clip, on the value scale. */
	ceiling: number;
	/** Nested out-of-fold, all human documents of the group (both points applied). */
	human: RateFigure;
	/** Of those, the essays by writers of English as a second language. */
	esl: RateFigure;
	/** Of those, the speeches. */
	speeches: RateFigure;
	/**
	 * The rates above are cross-validated on the fitting corpus, not confirmed on a held-out set (DESIGN CD-122, UD-35): the
	 * screen and the exports then say so beside them.
	 */
	crossValidated: boolean;
}

/** A length group's calibration error: ECE and the upper limit of its percentile 95% interval, both on the value scale [0, 1]. */
export interface CalibrationErrorFigure {
	ece: number;
	upper: number;
}

/** A's calibration maps (shared by `refit-gam-9c13f051` and `refit-gam-79c4b82e`): `calibration.ece` of either model JSON. */
const A_CALIBRATION_ERROR: Record<string, CalibrationErrorFigure> = {
	'B1-B2': { ece: 0.023798422071626496, upper: 0.08016302036524058 },
	'B3-B4': { ece: 0.03635574021702772, upper: 0.06702824796556581 },
	'B5-B6': { ece: 0.03022038158614111, upper: 0.05951202144844173 },
};

/**
 * One length bin of the claims table at the reference operating point (the nested figures, DESIGN CD-2 to CD-4): the human
 * documents flagged and the machine documents flagged. `machine` is null where the bin has fewer than 30 machine
 * documents (unmeasured). Bins are by words, `from` inclusive to `to` exclusive.
 */
export interface BinFigure {
	bin: string;
	from: number;
	to: number;
	human: RateFigure;
	machine: { n: number; flagged: number; lower: number } | null;
}

/**
 * The calibrated translation model's figures (`t2-gam-4ec263a0`; `data/oof/t2-claims.json`, `summary`): nested
 * out-of-fold, at its reference point. Gate G4 fails (a human-translation rate above 5%), so the model's index is evidence
 * and the headline keeps the uncalibrated wording; these figures say how far apart the classes sit.
 */
export interface TranslationFigures {
	modelId: string;
	humanOriginals: RateFigure;
	humanTranslations: RateFigure;
	machine: { n: number; flagged: number; lower: number };
	pairAuroc: { n: number; value: number; ci: [number, number] };
}

export interface ModelFigures {
	modelId: string;
	/** Nested out-of-fold, all human strata and lengths, at the reference point. */
	pooledHuman: RateFigure;
	/** The held-out final slice, scored once after sign-off; null until then (the line is hidden). */
	finalSliceHuman: RateFigure | null;
	/** Per human stratum, all lengths, at the reference point. */
	strata: StratumFigure[];
	/** The widest measured human cell, for the status line. */
	widestCell: RateFigure & { label: string };
	/** The held-out speech set, scored once after sign-off; null until then. */
	speechHeldout: RateFigure | null;
	/** The fixed caveat beside the index; null removes it (the speech measurements no longer call for it). */
	speechCaveat: SpeechCaveatText | null;
	/** Length groups whose calibration map cannot reach the reference point, by group id (e.g. "B1-B2"). */
	unreachable: Record<string, UnreachableFigure>;
	/** Length groups with their own operating point (CD-98), by group id; empty for a model without one. */
	shortPoint: Record<string, ShortPointFigure>;
	/** Per length bin: the human and machine documents flagged at the reference point(s); empty where the model card has none. */
	byBin: BinFigure[];
	/**
	 * The calibration error per length group (DESIGN ED-180): ECE with 15 equal-mass bins, nested out-of-fold, on the value
	 * scale, and the upper limit of its percentile 95% interval. Copied from the model JSON's `calibration.ece` (a test holds
	 * them equal); the dial quotes it beside the confidence reading and computes nothing.
	 */
	calibrationError: Record<string, CalibrationErrorFigure>;
	/** The model has no abstention band (its `abstainBelow` equals the reference threshold), so its status never reads so. */
	abstentionBandEmpty: boolean;
}

/** The translation model's figures (see `TranslationFigures`); a test holds them equal to `data/oof/t2-claims.json`. */
export const TRANSLATION_FIGURES: TranslationFigures = {
	modelId: 't2-gam-4ec263a0',
	humanOriginals: { n: 2633, flagged: 6, bound: 0.004512819980753565 },
	humanTranslations: { n: 593, flagged: 22, bound: 0.0529800495245474 },
	machine: { n: 430, flagged: 77, lower: 0.14920646488701983 },
	pairAuroc: {
		n: 430,
		value: 0.7071930773391022,
		ci: [0.6674042887874064, 0.7523244133786843],
	},
};

/** The model whose figures the interface shows when no analysed result names one; equals `DEFAULT_MODEL_ID` (a test holds the two together). */
export const SHIPPED_MODEL_ID = 'refit-gam-3c8d2de6';

/** The REFIT candidate (`refit-gam-9c13f051`): `data/refit/fit/claims.json`, kind fpr, operating point strict1, bin all. */
export const REFIT_FIGURES: ModelFigures = {
	modelId: 'refit-gam-9c13f051',
	/**
	 * The claims table has no pooled row, so this is the sum of the eleven whole-stratum rows below (n 4,105; 9 flagged;
	 * 2,978 authors) with the bound of `fprBound` in `tools/c3/claims.ts` (the larger of the Clopper-Pearson and the
	 * author-cluster-adjusted bound, ICC floored at 0.05).
	 */
	pooledHuman: { n: 4105, flagged: 9, bound: 0.0038426275631199824 },
	/** The locked final slice (access 3 of 3, 2026-10-19; `data/final-scores/refit-gam-79c4b82e.json`, all human, all lengths). */
	finalSliceHuman: { n: 2487, flagged: 3, bound: 0.003122907302509248 },
	strata: [
		{ stratum: 'H-ACA', label: 'academic', n: 478, flagged: 0, bound: 0.006247624254950538 },
		{ stratum: 'H-ENC', label: 'encyclopaedic', n: 132, flagged: 0, bound: 0.022439348489406666 },
		{
			stratum: 'H-ESL',
			label: 'English as a second language',
			n: 351,
			flagged: 0,
			bound: 0.008625457965857829,
		},
		{
			stratum: 'H-FIC-GEN',
			label: 'genre fiction',
			n: 508,
			flagged: 0,
			bound: 0.0059626566417889885,
		},
		{
			stratum: 'H-FIC-LIT',
			label: 'literary fiction',
			n: 460,
			flagged: 0,
			bound: 0.006655694138428401,
		},
		{
			stratum: 'H-LAW',
			label: 'legal and regulatory',
			n: 129,
			flagged: 0,
			bound: 0.024932464778995096,
		},
		{ stratum: 'H-NEWS', label: 'news', n: 298, flagged: 0, bound: 0.010398044860450817 },
		{ stratum: 'H-NF', label: 'non-fiction', n: 348, flagged: 0, bound: 0.008724824603073274 },
		{ stratum: 'H-SPEECH', label: 'speeches', n: 400, flagged: 7, bound: 0.03285965606547777 },
		{
			stratum: 'H-TRN',
			label: 'human translations',
			n: 659,
			flagged: 2,
			bound: 0.009789893535572268,
		},
		{ stratum: 'H-WEB', label: 'web text', n: 342, flagged: 0, bound: 0.008721198871510443 },
	],
	/**
	 * The measured human cell with the largest bound among the claims table's strict1, all-lengths cells (era, register
	 * and source-language cells included): period-English translations from Italian.
	 */
	widestCell: {
		label: 'human translations from Italian (period)',
		n: 34,
		flagged: 0,
		bound: 0.08653732794392982,
	},
	/** The held-out speeches (access 1 of 2, 2026-10-19; `data/refit/speech-heldout-scores.json`, G-R4: pooled B2 to B4). */
	speechHeldout: { n: 300, flagged: 1, bound: 0.015879463631819746 },
	// removed (CD-75 outcome "remove": G-R4 passes and every measured bin's point estimate is at most 3%)
	speechCaveat: null,
	unreachable: {
		'B1-B2': { maxWords: 1000, ceiling: 0.9427964593705859, tStar: 0.9882250824173245 },
	},
	shortPoint: {},
	calibrationError: A_CALIBRATION_ERROR,
	// the nested cells of `data/refit/b2b/short-point.json` (`nested.A`): B1 and B2 are A's, B3 and longer equal E-2's
	byBin: [
		{
			bin: 'B1',
			from: 150,
			to: 400,
			human: { n: 665, flagged: 0, bound: 0.004540630121840268 },
			machine: { n: 291, flagged: 0, lower: 0.0 },
		},
		{
			bin: 'B2',
			from: 400,
			to: 1000,
			human: { n: 771, flagged: 2, bound: 0.008173277632629749 },
			machine: { n: 221, flagged: 3, lower: 0.003709911347175843 },
		},
		{
			bin: 'B3',
			from: 1000,
			to: 3000,
			human: { n: 741, flagged: 3, bound: 0.010470802161566168 },
			machine: { n: 189, flagged: 10, lower: 0.02898301226616972 },
		},
		{
			bin: 'B4',
			from: 3000,
			to: 10000,
			human: { n: 637, flagged: 2, bound: 0.009881458742538395 },
			machine: { n: 178, flagged: 20, lower: 0.0757178431103014 },
		},
		{
			bin: 'B5',
			from: 10000,
			to: 30000,
			human: { n: 548, flagged: 2, bound: 0.011536477205356702 },
			machine: { n: 106, flagged: 60, lower: 0.48150165612229423 },
		},
		{
			bin: 'B6a',
			from: 30000,
			to: 60000,
			human: { n: 299, flagged: 0, bound: 0.010117924946248446 },
			machine: { n: 68, flagged: 50, lower: 0.6331590329203942 },
		},
		{
			bin: 'B6b',
			from: 60000,
			to: 1000000000,
			human: { n: 444, flagged: 0, bound: 0.006849954538689425 },
			machine: { n: 49, flagged: 37, lower: 0.6336206315384315 },
		},
	],
	abstentionBandEmpty: true,
};

/**
 * The E-2 candidate (`refit-gam-79c4b82e`, DESIGN CD-98, CD-103): A's file plus the B1–B2 short-text point.
 * `data/refit/b2b/claims.json`, kind fpr, operating point strict1 (either point flags), bin all; the short-text figures
 * are the nested B1 and B2 cells of `data/refit/b2b/short-point.json` (`nested.E-2.humanByBin`, `humanShortByStratum`).
 */
export const REFIT_E2_FIGURES: ModelFigures = {
	modelId: 'refit-gam-79c4b82e',
	/** The sum of the eleven whole-stratum rows below (n 4,105; 18 flagged; 2,978 authors) with the bound of `fprBound`. */
	pooledHuman: { n: 4105, flagged: 18, bound: 0.006519502328322935 },
	/** The locked final slice (access 3 of 3, 2026-10-19; `data/final-scores/refit-gam-79c4b82e.json`, all human, all lengths). */
	finalSliceHuman: { n: 2487, flagged: 3, bound: 0.003122907302509248 },
	strata: [
		{ stratum: 'H-ACA', label: 'academic', n: 478, flagged: 0, bound: 0.006247624254950538 },
		{ stratum: 'H-ENC', label: 'encyclopaedic', n: 132, flagged: 0, bound: 0.022439348489406666 },
		{
			stratum: 'H-ESL',
			label: 'English as a second language',
			n: 351,
			flagged: 4,
			bound: 0.026039746728517832,
		},
		{
			stratum: 'H-FIC-GEN',
			label: 'genre fiction',
			n: 508,
			flagged: 1,
			bound: 0.009390132224679668,
		},
		{
			stratum: 'H-FIC-LIT',
			label: 'literary fiction',
			n: 460,
			flagged: 0,
			bound: 0.006655694138428401,
		},
		{
			stratum: 'H-LAW',
			label: 'legal and regulatory',
			n: 129,
			flagged: 0,
			bound: 0.024932464778995096,
		},
		{ stratum: 'H-NEWS', label: 'news', n: 298, flagged: 1, bound: 0.01622951014771251 },
		{ stratum: 'H-NF', label: 'non-fiction', n: 348, flagged: 0, bound: 0.008724824603073274 },
		{ stratum: 'H-SPEECH', label: 'speeches', n: 400, flagged: 10, bound: 0.04229870743896391 },
		{
			stratum: 'H-TRN',
			label: 'human translations',
			n: 659,
			flagged: 2,
			bound: 0.009789893535572268,
		},
		{ stratum: 'H-WEB', label: 'web text', n: 342, flagged: 0, bound: 0.008721198871510443 },
	],
	/** As A's: period-English translations from Italian (no short document among them). */
	widestCell: {
		label: 'human translations from Italian (period)',
		n: 34,
		flagged: 0,
		bound: 0.08653732794392982,
	},
	/** The held-out speeches (access 1 of 2, 2026-10-19; `data/refit/speech-heldout-scores.json`, G-R4: pooled B2 to B4). */
	speechHeldout: { n: 300, flagged: 1, bound: 0.015879463631819746 },
	// removed (CD-75 outcome "remove": G-R4 passes and every measured bin's point estimate is at most 3%)
	speechCaveat: null,
	// B1–B2 has its own point, so no group is unreachable (CD-103) and the CD-94 sentence does not apply
	unreachable: {},
	shortPoint: {
		'B1-B2': {
			maxWords: 1000,
			name: 'short-text point',
			threshold: 2.8932464521664407,
			ceiling: 0.9427964593705859,
			human: { n: 1436, flagged: 11, bound: 0.012674326971104766 },
			esl: { n: 235, flagged: 4, bound: 0.03852557860507033 },
			speeches: { n: 123, flagged: 5, bound: 0.0837530798857281 },
			crossValidated: false,
		},
	},
	calibrationError: A_CALIBRATION_ERROR,
	// the nested cells of `data/refit/b2b/short-point.json` (`nested.E-2`); B1 and B2 count a flag by either point
	byBin: [
		{
			bin: 'B1',
			from: 150,
			to: 400,
			human: { n: 665, flagged: 3, bound: 0.01167102008261935 },
			machine: { n: 291, flagged: 9, lower: 0.016227542302024764 },
		},
		{
			bin: 'B2',
			from: 400,
			to: 1000,
			human: { n: 771, flagged: 8, bound: 0.0186821649811878 },
			machine: { n: 221, flagged: 17, lower: 0.049604135838907014 },
		},
		{
			bin: 'B3',
			from: 1000,
			to: 3000,
			human: { n: 741, flagged: 3, bound: 0.010470802161566168 },
			machine: { n: 189, flagged: 10, lower: 0.02898301226616972 },
		},
		{
			bin: 'B4',
			from: 3000,
			to: 10000,
			human: { n: 637, flagged: 2, bound: 0.009881458742538395 },
			machine: { n: 178, flagged: 20, lower: 0.0757178431103014 },
		},
		{
			bin: 'B5',
			from: 10000,
			to: 30000,
			human: { n: 548, flagged: 2, bound: 0.011536477205356702 },
			machine: { n: 106, flagged: 60, lower: 0.48150165612229423 },
		},
		{
			bin: 'B6a',
			from: 30000,
			to: 60000,
			human: { n: 299, flagged: 0, bound: 0.010117924946248446 },
			machine: { n: 68, flagged: 50, lower: 0.6331590329203942 },
		},
		{
			bin: 'B6b',
			from: 60000,
			to: 1000000000,
			human: { n: 444, flagged: 0, bound: 0.006849954538689425 },
			machine: { n: 49, flagged: 37, lower: 0.6336206315384315 },
		},
	],
	abstentionBandEmpty: true,
};

/**
 * The REFIT-S model (`refit-gam-3c8d2de6`, DESIGN CD-119 to CD-126, the default since 2.25.0): E-2 plus the LX-04 term for
 * texts of 150 to 999 prose words. Every figure is nested out-of-fold on the CV corpus (freeze v7) and read from
 * `data/refit/s/` (`claims.json` for the strata, `fit.json` for the pooled, short-text and per-bin cells, `ece.json` for the
 * calibration error, `gate-s7.json` for the held-out speeches); test/unit/refit-s-figures.test.ts holds them equal. The
 * short-text figures (150 to 999 words) are cross-validated, not confirmed on a held-out set (CD-122); the figures for 1,000
 * words and over are E-2's, because for those lengths this model is E-2 bit for bit (gate G-S4).
 */
export const REFIT_S_FIGURES: ModelFigures = {
	modelId: 'refit-gam-3c8d2de6',
	/** `fit.json` `nested.new.allHumanPooled` (n 4,105; 16 flagged; 2,900 authors), strict1, all lengths. */
	pooledHuman: { n: 4105, flagged: 16, bound: 0.005939469393157099 },
	/** Not read: the locked final slice was not scored for this model (its three accesses were spent on earlier models). */
	finalSliceHuman: null,
	strata: [
		{ stratum: 'H-ACA', label: 'academic', n: 478, flagged: 0, bound: 0.006247624254950538 },
		{ stratum: 'H-ENC', label: 'encyclopaedic', n: 132, flagged: 0, bound: 0.022439348489406666 },
		{
			stratum: 'H-ESL',
			label: 'English as a second language',
			n: 351,
			flagged: 0,
			bound: 0.008625457965857829,
		},
		{
			stratum: 'H-FIC-GEN',
			label: 'genre fiction',
			n: 508,
			flagged: 1,
			bound: 0.009390132224679668,
		},
		{
			stratum: 'H-FIC-LIT',
			label: 'literary fiction',
			n: 460,
			flagged: 1,
			bound: 0.010441631163264837,
		},
		{
			stratum: 'H-LAW',
			label: 'legal and regulatory',
			n: 129,
			flagged: 0,
			bound: 0.024932464778995096,
		},
		{ stratum: 'H-NEWS', label: 'news', n: 298, flagged: 1, bound: 0.01622951014771251 },
		{ stratum: 'H-NF', label: 'non-fiction', n: 348, flagged: 0, bound: 0.008724824603073274 },
		{ stratum: 'H-SPEECH', label: 'speeches', n: 400, flagged: 11, bound: 0.04537693670620742 },
		{
			stratum: 'H-TRN',
			label: 'human translations',
			n: 659,
			flagged: 2,
			bound: 0.009789893535572268,
		},
		{ stratum: 'H-WEB', label: 'web text', n: 342, flagged: 0, bound: 0.008721198871510443 },
	],
	/** As A's and E-2's: period-English translations from Italian (no short document among them). */
	widestCell: {
		label: 'human translations from Italian (period)',
		n: 34,
		flagged: 0,
		bound: 0.08653732794392982,
	},
	/**
	 * The held-out speeches (access 2 of 2, 2026-10-21, signed off by the operator; `data/refit/s/gate-s7.json`, G-S7): pooled
	 * B2 to B4. The B2 cell, the only one that can differ from E-2's read, is 0 of 100 flagged (G-S7 passed).
	 */
	speechHeldout: { n: 300, flagged: 1, bound: 0.015879463631819746 },
	// removed, as E-2's (CD-75): G-S7 passes and every measured bin's point estimate is at most 3%
	speechCaveat: null,
	// the B1–B2 map's top is 0.9900, above t* (0.9882), so no group is unreachable and the CD-94 sentence does not apply
	unreachable: {},
	shortPoint: {
		'B1-B2': {
			maxWords: 1000,
			name: 'short-text point',
			threshold: 3.030795638469417,
			ceiling: 0.99,
			// `fit.json` `nested.new.humanShortPooled` (1,183 authors) and `humanShortByStratum`
			human: { n: 1436, flagged: 9, bound: 0.010943230138004254 },
			esl: { n: 235, flagged: 0, bound: 0.01266688791445393 },
			speeches: { n: 123, flagged: 6, bound: 0.0941953599129302 },
			crossValidated: true,
		},
	},
	/** `data/refit/s/ece.json`: B1–B2 is this model's own map; B3–B4 and B5–B6 are E-2's (the model file's `calibration.ece` still carries E-2's B1–B2 value). */
	calibrationError: {
		'B1-B2': { ece: 0.025208406404087013, upper: 0.06401580264522905 },
		'B3-B4': { ece: 0.03635574021702772, upper: 0.06702824796556581 },
		'B5-B6': { ece: 0.03022038158614111, upper: 0.05951202144844173 },
	},
	// `fit.json` `nested.new` `humanByBin` and `machineByBin`; B3 and longer equal E-2's (G-S4)
	byBin: [
		{
			bin: 'B1',
			from: 150,
			to: 400,
			human: { n: 665, flagged: 3, bound: 0.01167102008261935 },
			machine: { n: 291, flagged: 37, lower: 0.0962594391044166 },
		},
		{
			bin: 'B2',
			from: 400,
			to: 1000,
			human: { n: 771, flagged: 6, bound: 0.015337555566902469 },
			machine: { n: 221, flagged: 52, lower: 0.1889729891863167 },
		},
		{
			bin: 'B3',
			from: 1000,
			to: 3000,
			human: { n: 741, flagged: 3, bound: 0.010470802161566168 },
			machine: { n: 189, flagged: 10, lower: 0.02898301226616972 },
		},
		{
			bin: 'B4',
			from: 3000,
			to: 10000,
			human: { n: 637, flagged: 2, bound: 0.009881458742538395 },
			machine: { n: 178, flagged: 20, lower: 0.0757178431103014 },
		},
		{
			bin: 'B5',
			from: 10000,
			to: 30000,
			human: { n: 548, flagged: 2, bound: 0.011536477205356702 },
			machine: { n: 106, flagged: 60, lower: 0.48150165612229423 },
		},
		{
			bin: 'B6a',
			from: 30000,
			to: 60000,
			human: { n: 299, flagged: 0, bound: 0.010117924946248446 },
			machine: { n: 68, flagged: 50, lower: 0.6331590329203942 },
		},
		{
			bin: 'B6b',
			from: 60000,
			to: 1000000000,
			human: { n: 444, flagged: 0, bound: 0.006849954538689425 },
			machine: { n: 49, flagged: 37, lower: 0.6336206315384315 },
		},
	],
	abstentionBandEmpty: true,
};

/** The C3 model's figures: `MODEL_CARD_FIGURES` unchanged, with the speech caveat the interface has always shown. */
export const C3_FIGURES: ModelFigures = {
	modelId: MODEL_CARD_FIGURES.modelId,
	pooledHuman: MODEL_CARD_FIGURES.pooledHuman,
	finalSliceHuman: MODEL_CARD_FIGURES.finalSliceHuman,
	strata: MODEL_CARD_FIGURES.strata,
	widestCell: MODEL_CARD_FIGURES.widestCell,
	speechHeldout: null,
	speechCaveat: SPEECH_CAVEAT,
	unreachable: {},
	shortPoint: {},
	calibrationError: {
		'B1-B2': { ece: 0.029458578130862702, upper: 0.08588329602247621 },
		'B3-B4': { ece: 0.03081700129327872, upper: 0.07363395126616894 },
		'B5-B6': { ece: 0.027476779193506017, upper: 0.06528581094816241 },
	},
	// the C3 card gives no pooled per-bin cells in a form a test can hold to its claims file
	byBin: [],
	abstentionBandEmpty: true,
};

const FIGURES: Record<string, ModelFigures> = {
	[C3_FIGURES.modelId]: C3_FIGURES,
	[REFIT_FIGURES.modelId]: REFIT_FIGURES,
	[REFIT_E2_FIGURES.modelId]: REFIT_E2_FIGURES,
	[REFIT_S_FIGURES.modelId]: REFIT_S_FIGURES,
};

/** The figures of a bundled authorship model; an id with none (an uploaded or unknown model) falls back to the shipped model's. */
export function figuresFor(id: string | null | undefined): ModelFigures {
	return (
		(hasFigures(id) ? FIGURES[id as string] : undefined) ??
		(FIGURES[SHIPPED_MODEL_ID] as ModelFigures)
	);
}

/** Whether the id is a bundled authorship model with figures (and a model card of its own). */
export const hasFigures = (id: string | null | undefined): boolean =>
	id !== null && id !== undefined && Object.hasOwn(FIGURES, id);

/** The model card of a model, as the app serves it. */
export const modelCardHrefFor = (id: string): string => `/docs/model-card/${id}.md`;
